import { AxiosResponse } from "axios";
import _ from "lodash";
import { oc } from "ts-optchain";

// Services
import { DealernetApi } from "./DealernetApi";

// Types
import {
  IAccountAddressDto,
  IAddressBookAddressDto,
  ICustomerAddressDto,
  ICountryDto,
  IPagedList,
  IPostalAddress,
  Nullable,
} from "@/types";

export const ADDRESS_BOOK_PATH = "AddressBook";

export class AddressBookService {
  public static async GetAddressBookCustomers(
    accountId: number,
    userId: number,
    addressFilter: string,
    page: number,
    pageSize: number,
    sortBy: string,
    sortDesc: boolean
  ): Promise<IPagedList<ICustomerAddressDto>> {
    const response = await DealernetApi.client().request({
      method: "get",
      url: `${ADDRESS_BOOK_PATH}/LoadCustomerAddresses`,
      params: {
        accountId,
        userId,
        addressFilter,
        page,
        pageSize,
        sortBy,
        sortDesc,
      },
    });

    return oc(response as AxiosResponse<IPagedList<ICustomerAddressDto>>).data()!;
  }

  public static async GetEmergencyShipToAddress(accountId: number, userId: number): Promise<IAddressBookAddressDto> {
    const response = await DealernetApi.client().request({
      method: "get",
      url: `${ADDRESS_BOOK_PATH}/GetEmergencyShipToAddress`,
      params: {
        accountId,
        userId,
      },
    });

    return oc(response as AxiosResponse<IAddressBookAddressDto>).data()!;
  }

  public static async SaveEmergencyShipToAddress(
    accountId: number,
    userId: number,
    addressBookAddressDto: IAddressBookAddressDto
  ): Promise<boolean> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${ADDRESS_BOOK_PATH}/SaveEmergencyShipToAddress`,
      params: {
        accountId,
        userId,
      },
      data: addressBookAddressDto,
    });

    return oc(response as AxiosResponse<boolean>).data()!;
  }

  public static async SaveCustomerAddress(
    accountId: number,
    userId: number,
    customerAddressDto: ICustomerAddressDto
  ): Promise<boolean> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${ADDRESS_BOOK_PATH}/SaveCustomerAddress`,
      params: {
        accountId,
        userId,
      },
      data: customerAddressDto,
    });

    return oc(response as AxiosResponse<boolean>).data()!;
  }

  public static async DeleteEmergencyShipToAddress(
    accountId: number,
    userId: number,
    addressId: number
  ): Promise<boolean> {
    const response = await DealernetApi.client().request({
      method: "delete",
      url: `${ADDRESS_BOOK_PATH}/DeleteCustomerAddress`,
      params: {
        accountId,
        userId,
        addressId,
      },
    });

    return oc(response as AxiosResponse<boolean>).data()!;
  }

  public static async DeleteCustomerAddress(accountId: number, userId: number, customerId: number): Promise<boolean> {
    const response = await DealernetApi.client().request({
      method: "delete",
      url: `${ADDRESS_BOOK_PATH}/DeleteCustomer`,
      params: {
        accountId,
        userId,
        customerId,
      },
    });

    return oc(response as AxiosResponse<boolean>).data()!;
  }

  public static async GetCountries(accountId: number, userId: number): Promise<ICountryDto> {
    const response = await DealernetApi.client().request({
      method: "get",
      url: `${ADDRESS_BOOK_PATH}/GetCountries`,
      params: {
        accountId,
        userId,
      },
    });

    return oc(response as AxiosResponse<ICountryDto>).data()!;
  }

  public static ConvertCustomerAddressToPostalAddress(
    address?: Nullable<ICustomerAddressDto>
  ): IPostalAddress {
    return {
      Name: address?.CustomerName ?? "",
      AddressLine1: address?.AddressLine1 ?? "",
      AddressLine2: address?.AddressLine2 ?? "",
      AddressLine3: "",
      City: address?.City ?? "",
      StateProvinceCode: address?.StateProvince ?? "",
      PostalCode: _.trim(address?.PostalCode ?? ""),
      CountryCode: address?.Country ?? "",
    };
  }

  public static ConvertAccountAddressToPostalAddress(
    address?: Nullable<IAccountAddressDto>,
    name?: string
  ): IPostalAddress {
    return {
      Name: name,
      AddressLine1: address?.Street1 ?? "",
      AddressLine2: address?.Street2 ?? "",
      AddressLine3: address?.Street3 ?? "",
      City: address?.City ?? "",
      StateProvinceCode: address?.StateProvinceCode ?? "",
      PostalCode: _.trim(address?.PostalCode ?? ""),
      CountryCode: address?.CountryCode ?? "",
    };
  }

  public static ConvertAddressBookAddressToPostalAddress(
    address?: Nullable<IAddressBookAddressDto>,
    name?: string
  ): IPostalAddress {
    return {
      Id: address?.Id ?? 0,
      Name: name ?? address?.AddressLine1 ?? "Name Not Found",
      AddressLine1: address?.AddressLine1 ?? "",
      AddressLine2: address?.AddressLine2 ?? "",
      AddressLine3: "",
      City: address?.City ?? "",
      StateProvinceCode: address?.State ?? "",
      PostalCode: _.trim(address?.PostalCode ?? ""),
      CountryCode: address?.Country ?? "",
    };
  }
}
